import { useOutletContext } from "react-router";

import { ClientDetailResponse } from "@/api";
import { PortalAccessView } from "@/common/components/portal-access/PortalAccessView";
import { usePortalAccount } from "@/common/components/portal-access/hooks/usePortalAccount";
import { PortalAccessTexts } from "@/common/components/portal-access/portalAccess.types";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { createClientPortalAccountApi } from "./clientPortalAccountApi";
import { ClientInvitationCard } from "./components/ClientInvitationCard";

/**
 * The client entry (Kund:innen-Zugang) of the shared portal-access page: wires the client API
 * adapter, the client-specific sentences and the address-selection card into PortalAccessView.
 */
export function ClientAccessView() {
  const client = useOutletContext<ClientDetailResponse>();
  const { t } = useTranslation();

  const texts: PortalAccessTexts = {
    title: t("clientAccess.title"),
    description: t("clientAccess.description"),
    pendingDescription: t("clientAccess.pending.description"),
    confirmLockText: t("clientAccess.manage.confirmLockText"),
    confirmUnlockText: t("clientAccess.manage.confirmUnlockText"),
    confirmResetText: (email) => t("clientAccess.manage.confirmResetText", { email }),
  };

  const account = usePortalAccount({
    api: createClientPortalAccountApi(client.clientId),
    statusQueryKey: [QueryKey.ClientPortalAccountStatus, client.clientId],
    // Every action writes a case note (Aktenvermerk); a freed address also unblocks the activation
    // form of whichever other client case file (Kundenakt) was shown the duplicate hint for it,
    // which is why the availability key is deliberately not scoped to this client.
    extraInvalidationKeys: [[QueryKey.ClientCaseNotes, client.clientId], [QueryKey.PortalEmailAvailability]],
    texts,
  });

  return (
    <PortalAccessView
      account={account}
      texts={texts}
      invitationCard={
        <ClientInvitationCard clientId={client.clientId} onSend={account.onSendInvitation} isSending={account.isBusy} />
      }
      showIdentityDocument
    />
  );
}
